<script setup lang="ts">
import { computed } from 'vue';
import VueDraggable from 'vuedraggable';
import type { SortableEvent } from 'sortablejs';

const props = withDefaults(
    defineProps<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: Record<string, any>[];
        itemKey: string;
        selected?: string | number;
        draggable?: boolean;
        hideCounter?: boolean;
    }>(),
    { draggable: true, hideCounter: false }
);

const emit = defineEmits<{
    (e: 'input', value: Record<string, unknown>[]): void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: 'row-click', item: any, index: number): void;
    (e: 'drag-end', item: SortableEvent): void;
}>();

const _model = computed({
    get: () => props.value,
    set: (value: Record<string, unknown>[]) => {
        emit('input', value);
    }
});

const isSelected = computed(() => {
    return (item: Record<string, unknown>) => {
        return item[props.itemKey] === props.selected;
    };
});
</script>

<template>
    <div v-if="_model.length" class="draggable-list-wrapper">
        <vue-draggable
            v-model="_model"
            class="list"
            :animation="200"
            :disabled="!draggable"
            @end="emit('drag-end', $event)"
        >
            <div
                v-for="(element, index) in _model"
                :key="element[itemKey]"
                class="item-row"
                :class="{ 'bg-purple-lightest': isSelected(element) }"
                @click="emit('row-click', element, index)"
            >
                <font-awesome-icon
                    v-if="draggable && _model.length > 1"
                    class="mr-3"
                    :icon="['fas', 'grip-dots-vertical']"
                />
                <span v-if="!hideCounter" class="counter"> {{ index + 1 }}. </span>
                <slot name="item" v-bind="{ element, index, selected: isSelected(element) }" />
            </div>
        </vue-draggable>
    </div>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';
.draggable-list-wrapper {
    border: 1px solid get-color('gray-light');
    border-radius: 0.25rem;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 0.375rem;
        height: 0.375rem;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
        border: none;
        box-shadow: unset;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #e6e6e6;
    }

    .list {
        max-height: 205px;
        .item-row {
            display: flex;
            align-items: center;
            cursor: pointer;
            padding: 0.625rem;
            border-bottom: 1px solid get-color('gray-light');
            .counter {
                color: #919191;
                margin-right: 0.25rem;
            }
            &:last-child {
                border-bottom: none;
            }
            &:hover {
                background-color: get-color('purple-lightest');
            }
        }
    }
}
</style>
